/**
 * Reusable form fields shared by the create-custom-channel modal and the
 * inline OSS-channel editor on the unit-detail panel. Stateless — values
 * and onChange come from the parent.
 */

const TYPES = [
  { value: 'chat',      label: 'Chat',      hint: 'Many-to-many conversational channel.' },
  { value: 'order',     label: 'Order',     hint: 'Formal orders with signatures, validity, and ACK (Stage 2).' },
  { value: 'broadcast', label: 'Broadcast', hint: 'One-to-many: only writers can post; everyone else read-only.' },
];

const PRIORITIES = ['routine', 'priority', 'immediate', 'flash'];

// Hot-fix #11 — enabled data-types catalog. Two mutually-exclusive
// categories chat/map (a channel carries either chat content OR
// map/situational data, never both). The form enforces XOR by
// auto-clearing the inactive category when a checkbox in the other
// one is toggled on.
//
// 2026-06-19 — PoC (Push-to-Talk over Cellular) is the third
// category but ORTHOGONAL — it does NOT participate in the XOR.
// Voice/video sessions can coexist with chat (operators talk while
// chatting) AND with map (commander confirms order verbally on a
// data-focused channel). When at least one poc_* token is checked,
// the form surfaces pocMode + pocRecording controls below the grid.
const DATA_TYPE_CATEGORIES = [
  {
    id: 'chat',
    title: 'Chat content',
    icon: 'fa-comments',
    items: [
      { value: 'text',         label: 'Text',         icon: 'fa-message',    hint: 'Plain-text messages.' },
      { value: 'audio_notes',  label: 'Audio notes',  icon: 'fa-microphone', hint: 'Voice recordings attached to chat.' },
      { value: 'images',       label: 'Images',       icon: 'fa-image',      hint: 'Photos, screenshots, sketches.' },
      { value: 'files',        label: 'Files',        icon: 'fa-paperclip',  hint: 'Arbitrary file attachments.' },
    ],
  },
  {
    id: 'map',
    title: 'Map / situational data',
    icon: 'fa-map-location-dot',
    items: [
      { value: 'cop_layers',     label: 'COP Layers',     icon: 'fa-layer-group',  hint: 'Federation / vendor operational layers from ad-cop-service.' },
      { value: 'awds_layers',    label: 'AWDS Layers',    icon: 'fa-cloud-sun',    hint: 'Atmospheric / oceanographic overlays (weather, fronts, sea state).' },
      { value: 'cot_layers',     label: 'CoT Layers',     icon: 'fa-crosshairs',   hint: 'TAK / Cursor-on-Target tracks decoded by COP.' },
      { value: 'nffi_layers',    label: 'NFFI Layers',    icon: 'fa-people-group', hint: 'NATO Friendly Force Information (STANAG 5527/5528) positions.' },
      { value: 'adatp3_layers',  label: 'ADatP-3 Layers', icon: 'fa-file-lines',   hint: 'NATO ADatP-3 messages with coordinates (NBC-1, MEDEVAC, SITREP, …).' },
      // Stage-3.4 §3.9 / §3.10 — terminal-authored map data flowing
      // through ad-cop-service ingest endpoints (v2.72+). Receivers
      // consume the resulting auto-created cop-l-loc-{unit} /
      // cop-l-intel-{user} layers via the same federation pipeline
      // as cop_layers; the dataType tag here tells the home node
      // (and ultimately the mobile broadcaster) WHICH per-unit /
      // per-user layer to author into.
      { value: 'location',       label: 'Locations (own)',  icon: 'fa-location-crosshairs', hint: 'Terminal’s own GPS position, periodic. Auto-creates cop-l-loc-{unit} on ad-cop-service.' },
      { value: 'intel',          label: 'Intelligence',     icon: 'fa-binoculars',          hint: 'Operator-drawn recon markers from the DRAW tool. Auto-creates cop-l-intel-{user} on ad-cop-service. Depends on §3.4 to be useful.' },
      // 2026-06-09 — Air-targets live feed from upstream sensors
      // microservice (radar + RF-detector tracks). Pure pass-through
      // on the home node — no storage, no retention. See
      // manuals/NODE-targets/PLAN.md.
      { value: 'targets',        label: 'Targets',          icon: 'fa-jet-fighter',         hint: 'Live air-targets feed (radar + RF-detector tracks). Real-time pass-through from upstream sensors microservice. No storage, no retention.' },
    ],
  },
  {
    id: 'poc',
    title: 'PoC voice/video (orthogonal — can pair with chat or map)',
    icon: 'fa-phone-volume',
    items: [
      { value: 'poc_voice', label: 'Voice (PoC)', icon: 'fa-phone-volume', hint: 'Real-time push-to-talk voice sessions via LiveKit. Routes through ad-tms-service on each node.' },
      { value: 'poc_video', label: 'Video (PoC)', icon: 'fa-video',        hint: 'Real-time push-to-talk video sessions via LiveKit. Recipient must opt in to receive camera; sender hardware-button-gated.' },
    ],
  },
];

const CHAT_TYPES = new Set(DATA_TYPE_CATEGORIES[0].items.map((i) => i.value));
const MAP_TYPES  = new Set(DATA_TYPE_CATEGORIES[1].items.map((i) => i.value));
const POC_TYPES  = new Set(DATA_TYPE_CATEGORIES[2].items.map((i) => i.value));

function categoryOf(typeValue) {
  if (CHAT_TYPES.has(typeValue)) return 'chat';
  if (MAP_TYPES.has(typeValue))  return 'map';
  if (POC_TYPES.has(typeValue))  return 'poc';
  return null;
}

// XOR is enforced between chat and map ONLY. PoC is orthogonal —
// activeCategoryOf returns null for a poc-only selection (no XOR
// effect on the grid) and ignores poc tokens when scanning a mixed
// selection. This means picking a poc_* checkbox never clears
// chat/map and vice versa.
function activeCategoryOf(list) {
  for (const t of list || []) {
    const c = categoryOf(t);
    if (c === 'chat' || c === 'map') return c;
  }
  return null;
}

function hasPocSelection(list) {
  return (list || []).some((t) => POC_TYPES.has(t));
}

/**
 * Reusable channel-settings form. Two opt-in fields are surfaced
 * conditionally:
 *   • homeNodeOnly — OSS only (custom channels are mesh-wide by design)
 *   • archived     — edit only (you don't create something pre-archived)
 *
 * Pass `isOss` and `isEdit` so the same component covers all four
 * caller contexts (OSS-create, OSS-edit, custom-create, custom-edit).
 */
export function ChannelSettingsFields({ values, errors, onChange, isOss, isEdit = false, busy }) {
  function update(field, v) {
    onChange({ ...values, [field]: v });
  }

  return (
    <div className="space-y-4">
      {/* Slug — OSS-create only. Surfacing here lets a unit host many
          OSS channels disambiguated by the trailing path segment. Empty
          slug means the unit's primary channel (legacy 1-per-unit
          path). Slug is a path identity and is fixed at creation —
          editing it would change the path and break references on the
          mesh side, so we suppress the field on edit. */}
      {isOss && !isEdit && (
        <div>
          <label htmlFor="ch-slug" className="field-label">
            Slug <span className="font-normal text-mist">(optional)</span>
          </label>
          <input
            id="ch-slug"
            className="field font-mono"
            value={values.slug || ''}
            onChange={(e) => update('slug', e.target.value.toLowerCase())}
            placeholder="e.g. orders, layers-cop, awds"
            maxLength={64}
            disabled={busy}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="mt-1 text-[11px] leading-snug text-mist">
            Leave empty for the unit's primary channel. With a slug, the path becomes
            <span className="mx-1 font-mono text-ink">/&hellip;/&lt;unit&gt;/&lt;slug&gt;</span>
            and you can have many OSS channels per unit. Lowercase a-z 0-9 with
            <span className="mx-1 font-mono text-ink">-</span>
            or
            <span className="mx-1 font-mono text-ink">_</span>.
          </p>
          {errors?.slug && (
            <p className="mt-1 text-[11px] font-medium text-rose-700">
              <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden="true" />
              {errors.slug}
            </p>
          )}
        </div>
      )}

      {/* Type as radio cards */}
      <div>
        <label className="field-label">Type</label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {TYPES.map((t) => {
            const active = values.type === t.value;
            return (
              <label
                key={t.value}
                className={
                  'flex cursor-pointer items-start gap-3 rounded-2xl border px-3 py-2.5 transition-colors ' +
                  (active ? 'border-accent bg-accent-soft' : 'border-line bg-white hover:bg-canvas')
                }
              >
                <input
                  type="radio"
                  name="ch-type"
                  className="mt-1 h-4 w-4 accent-accent"
                  value={t.value}
                  checked={active}
                  onChange={() => update('type', t.value)}
                  disabled={busy}
                />
                <span>
                  <span className="block text-sm font-semibold text-ink">{t.label}</span>
                  <span className="block text-[11px] leading-snug text-mist">{t.hint}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Scope (OSS only) and priority side by side */}
      <div className={`grid grid-cols-1 gap-4 ${isOss ? 'sm:grid-cols-2' : 'sm:grid-cols-1'}`}>
        {isOss && (
          <div>
            <label className="field-label">Scope</label>
            <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
              {[
                { v: 'direct',  label: 'Direct',  hint: 'Only personnel of this unit.' },
                { v: 'subtree', label: 'Subtree', hint: 'This unit and all its descendants.' },
              ].map((opt) => {
                const active = values.scope === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => update('scope', opt.v)}
                    title={opt.hint}
                    disabled={busy}
                    className={
                      'flex flex-1 items-center justify-center gap-1 rounded-xl px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ' +
                      (active ? 'bg-accent-soft text-accent' : 'text-mist hover:text-ink')
                    }
                  >
                    <i className={`fa-solid ${opt.v === 'subtree' ? 'fa-folder-tree' : 'fa-folder'} text-[10px]`} aria-hidden="true" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div>
          <label htmlFor="ch-priority" className="field-label">Priority</label>
          <select
            id="ch-priority"
            className="field"
            value={values.priority}
            onChange={(e) => update('priority', e.target.value)}
            disabled={busy}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Data types — categorical checkbox grid (hot-fix #11). Two
          mutually-exclusive categories: chat content vs map data. The
          form auto-empties the inactive category when the operator
          toggles a checkbox in the other one — there's no way to land
          in a mixed state from the UI. The server's validateDataTypes
          enforces the same rule so direct API hits also can't mix. */}
      <DataTypesGrid
        values={values}
        onChange={onChange}
        busy={busy}
        error={errors?.dataTypes}
      />


      {/* Description */}
      <div>
        <label htmlFor="ch-desc" className="field-label">
          Description <span className="font-normal text-mist">(optional)</span>
        </label>
        <textarea
          id="ch-desc"
          className="field min-h-[70px] resize-y"
          value={values.description || ''}
          onChange={(e) => update('description', e.target.value)}
          maxLength={1024}
          disabled={busy}
        />
      </div>

      {/* Roaming policy — OSS-only. Custom channels are mesh-wide by
          design and the server refuses home_node_only=true on them. */}
      {isOss && (
        <label className="flex items-start gap-3 rounded-2xl border border-line bg-canvas px-4 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-line accent-warning"
            checked={!!values.homeNodeOnly}
            onChange={(e) => update('homeNodeOnly', e.target.checked)}
            disabled={busy}
          />
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-ink">
              <i className="fa-solid fa-lock text-warning text-[11px]" aria-hidden="true" />
              Home node only
            </div>
            <div className="text-xs text-mist">
              When enabled, this channel is only visible / writable on its effective home node — terminals
              connected through a foreign node won't see it. Default is off (mesh-wide). Requires the unit
              chain to have a home node anchored.
            </div>
          </div>
        </label>
      )}

      {/* Archived — edit-only. You don't create a pre-archived channel,
          so the toggle is suppressed during CREATE. Lives in the shared
          fields so OSS-edit and custom-edit modals both surface it. */}
      {isEdit && (
        <label className="flex items-start gap-3 rounded-2xl border border-line bg-canvas px-4 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-line accent-accent"
            checked={!!values.archived}
            onChange={(e) => update('archived', e.target.checked)}
            disabled={busy}
          />
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-ink">
              <i className="fa-solid fa-box-archive text-mist text-[11px]" aria-hidden="true" />
              Archived
            </div>
            <div className="text-xs text-mist">
              Read-only display; no new writes. Existing messages stay visible. Toggle off to revive.
            </div>
          </div>
        </label>
      )}
    </div>
  );
}

export const DEFAULT_CHANNEL_VALUES = {
  name: '',
  // 2026-05 — OSS slug. Empty = primary channel of the unit (legacy
  // 1-per-unit path); when set, becomes the trailing path segment and
  // allows many OSS channels per unit.
  slug: '',
  type: 'chat',
  scope: 'direct',
  priority: 'routine',
  // Retention is no longer surfaced in the form (hot-fix #11) — the
  // numeric TTL knobs were superseded by the data-types checkboxes. The
  // columns stay in DB for now for backwards compat; if a future TTL
  // engine wants them, we expose them as a separate settings card.
  retention: { textDays: null, audioDays: null, imageDays: null, filesDays: null },
  description: '',
  homeNodeOnly: false,
  archived: false,
  // hot-fix #11 — by default a fresh channel allows just text. Operator
  // adds more types from the form's grid; chat / map categories are
  // mutually exclusive, PoC is orthogonal.
  dataTypes: ['text'],
  // hot-fix #12 — single retention window for chat-category channels.
  // null = unlimited.
  chatRetentionDays: null,
  // 2026-06-19 — PoC defaults. pocMode auto-resolves to 'simplex' on
  // server when a poc_* token is in dataTypes and pocMode is null,
  // but the form still ships an explicit value so operator's choice
  // is round-trip-stable.
  pocMode: 'simplex',
  pocRecording: false,
  // 2026-06-20 — PTT max transmission seconds. null = unlimited.
  // Default 180 s when a poc_* token is enabled.
  pocPttMaxSec: 180,
};

/**
 * Categorical checkbox grid for the enabled data-types on a channel.
 * Operator picks any subset within ONE category. Toggling a checkbox in
 * the inactive category auto-clears the active one — there's no way to
 * end up with a mixed selection.
 */
function DataTypesGrid({ values, onChange, busy, error }) {
  const list   = Array.isArray(values.dataTypes) ? values.dataTypes : [];
  const active = activeCategoryOf(list);  // chat | map | null  (poc never returned)
  const pocOn  = hasPocSelection(list);

  function toggle(typeValue) {
    const isOn = list.includes(typeValue);
    const cat  = categoryOf(typeValue);
    let nextList;
    if (isOn) {
      // Toggling off — keep within current category.
      nextList = list.filter((t) => t !== typeValue);
    } else if (cat === 'poc') {
      // PoC is orthogonal — just add it, no XOR effect.
      nextList = [...list, typeValue];
    } else if (active && active !== cat) {
      // Crossing chat↔map boundary — drop chat/map tokens (keep any
      // poc_* tokens already enabled — they're orthogonal). Reset
      // chat-only retention since it doesn't apply to the map category.
      nextList = [typeValue, ...list.filter((t) => categoryOf(t) === 'poc')];
      onChange({ ...values, dataTypes: nextList, chatRetentionDays: null });
      return;
    } else {
      nextList = [...list, typeValue];
    }
    onChange({ ...values, dataTypes: nextList });
  }

  function setChatRetention(v) {
    onChange({ ...values, chatRetentionDays: v });
  }

  function setPocMode(v) { onChange({ ...values, pocMode: v }); }
  function setPocRecording(v) { onChange({ ...values, pocRecording: v }); }
  function setPocPttMaxSec(v) { onChange({ ...values, pocPttMaxSec: v }); }

  return (
    <div>
      <div className="field-label flex items-center gap-2">
        Data types
        <span className="font-normal text-mist">(at least one; chat and map can't mix; PoC is orthogonal)</span>
      </div>

      <div className="space-y-3">
        {DATA_TYPE_CATEGORIES.map((cat) => {
          // XOR-induced dimming applies ONLY to the chat/map block
          // that's currently inactive. PoC block is never dimmed.
          const otherActive = cat.id !== 'poc' && active && active !== cat.id;
          return (
            <fieldset
              key={cat.id}
              className={
                'rounded-2xl border p-3 transition-colors ' +
                (otherActive
                  ? 'border-line bg-canvas/50 opacity-60'
                  : 'border-line bg-white')
              }
            >
              <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-mist">
                <i className={`fa-solid ${cat.icon} text-[10px] mr-1.5`} aria-hidden="true" />
                {cat.title}
              </legend>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {cat.items.map((it) => {
                  const checked = list.includes(it.value);
                  return (
                    <label
                      key={it.value}
                      className={
                        'flex cursor-pointer items-center gap-2 rounded-xl border px-2.5 py-1.5 text-sm transition-colors ' +
                        (checked
                          ? 'border-accent bg-accent-soft text-accent'
                          : 'border-line bg-white text-ink hover:bg-canvas')
                      }
                      title={it.hint}
                    >
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-accent"
                        checked={checked}
                        onChange={() => toggle(it.value)}
                        disabled={busy}
                      />
                      <i className={`fa-solid ${it.icon} text-[11px] ${checked ? '' : 'text-mist'}`} aria-hidden="true" />
                      <span className="text-[12px]">{it.label}</span>
                    </label>
                  );
                })}
              </div>

              {/* hot-fix #12 — single retention window for chat-category
                  channels. Hidden when the map category is active (it
                  doesn't apply to layered situational data — those have
                  their own lifecycle in cop_* tables). */}
              {cat.id === 'chat' && active === 'chat' && (
                <div className="mt-3 flex items-center gap-3 border-t border-line/60 pt-3">
                  <label htmlFor="ch-retention" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-mist whitespace-nowrap">
                    <i className="fa-solid fa-clock-rotate-left text-[10px]" aria-hidden="true" />
                    Auto-delete after
                  </label>
                  <input
                    id="ch-retention"
                    type="number"
                    min="0"
                    max="36500"
                    className="field font-mono text-[13px] w-28"
                    placeholder="∞"
                    value={values.chatRetentionDays ?? ''}
                    onChange={(e) => setChatRetention(e.target.value === '' ? null : Number(e.target.value))}
                    disabled={busy}
                  />
                  <span className="text-xs text-mist">
                    days {values.chatRetentionDays == null && <em>(empty = keep forever)</em>}
                  </span>
                </div>
              )}

              {/* 2026-06-19 — PoC sub-section appears when any poc_*
                  token is enabled. Two controls: mode (simplex/duplex
                  radio) + recording toggle. Recording warns operator
                  that audio is captured server-side. */}
              {cat.id === 'poc' && pocOn && (
                <div className="mt-3 space-y-3 border-t border-line/60 pt-3">
                  <div className="flex items-start gap-3">
                    <span className="flex items-center gap-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-mist whitespace-nowrap">
                      <i className="fa-solid fa-users-line text-[10px]" aria-hidden="true" />
                      Mode
                    </span>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 flex-1">
                      {[
                        { v: 'simplex', label: 'Simplex (queue)',     hint: 'Single speaker at a time. Push-to-talk floor queue: one transmits, others listen.' },
                        { v: 'duplex',  label: 'Duplex (mixed audio)', hint: 'All members can talk simultaneously. LiveKit SFU mixes streams.' },
                      ].map((opt) => {
                        const on = (values.pocMode || 'simplex') === opt.v;
                        return (
                          <label
                            key={opt.v}
                            title={opt.hint}
                            className={
                              'flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ' +
                              (on ? 'border-accent bg-accent-soft text-accent' : 'border-line bg-white text-ink hover:bg-canvas')
                            }
                          >
                            <input
                              type="radio"
                              name="ch-poc-mode"
                              className="mt-0.5 h-3.5 w-3.5 accent-accent"
                              value={opt.v}
                              checked={on}
                              onChange={() => setPocMode(opt.v)}
                              disabled={busy}
                            />
                            <span className="text-[12px] leading-tight">{opt.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <label className="flex items-start gap-3 rounded-xl border border-line bg-canvas/40 px-3 py-2">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-3.5 w-3.5 accent-warning"
                      checked={!!values.pocRecording}
                      onChange={(e) => setPocRecording(e.target.checked)}
                      disabled={busy}
                    />
                    <span className="flex-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-ink">
                        <i className="fa-solid fa-record-vinyl text-warning text-[11px]" aria-hidden="true" />
                        Record sessions
                      </span>
                      <span className="block text-[11px] leading-snug text-mist">
                        LiveKit Egress on the home node captures each session and uploads via federation.
                        Retention controlled by Settings → System → <em>retention_poc_recordings_days</em>.
                      </span>
                    </span>
                  </label>

                  {/* 2026-06-20 — PTT max transmission seconds. Empty
                      = unlimited. Default 180 s. Mobile auto-stops PTT
                      at this limit. Mobile UI shows the cap (or «без
                      лимита») in the channel header on the PoC page. */}
                  <div className="flex items-center gap-3 rounded-xl border border-line bg-canvas/40 px-3 py-2">
                    <label htmlFor="ch-poc-ptt-max" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-mist whitespace-nowrap">
                      <i className="fa-solid fa-stopwatch text-[10px]" aria-hidden="true" />
                      Max PTT
                    </label>
                    <input
                      id="ch-poc-ptt-max"
                      type="number"
                      min="0"
                      max="3600"
                      className="field font-mono text-[13px] w-28"
                      placeholder="∞"
                      value={values.pocPttMaxSec ?? ''}
                      onChange={(e) => setPocPttMaxSec(e.target.value === '' ? null : Number(e.target.value))}
                      disabled={busy}
                    />
                    <span className="text-xs text-mist">
                      seconds {values.pocPttMaxSec == null && <em>(empty = unlimited)</em>}
                    </span>
                  </div>
                </div>
              )}
            </fieldset>
          );
        })}
      </div>
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
      {list.length === 0 && (
        <p className="mt-1 text-xs text-rose-600">Pick at least one data type.</p>
      )}
    </div>
  );
}
